import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const OTPForm = ({ onSubmit, loading }) => {
  const [step, setStep] = useState('phone'); // 'phone' or 'verify'
  const [formData, setFormData] = useState({
    phone: '',
    otp: ''
  });
  const [errors, setErrors] = useState({});
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    } else if (timer === 0 && step === 'verify') {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer, step]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validatePhone = () => {
    const newErrors = {};
    if (!formData?.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-()]{10,}$/?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateOTP = () => {
    const newErrors = {};
    if (!formData?.otp) {
      newErrors.otp = 'OTP is required';
    } else if (formData?.otp?.length !== 6) {
      newErrors.otp = 'OTP must be 6 digits';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSendOTP = (e) => {
    e?.preventDefault();
    if (validatePhone()) {
      setStep('verify');
      setTimer(60);
      setCanResend(false);
    }
  };

  const handleVerifyOTP = (e) => {
    e?.preventDefault();
    if (validateOTP()) {
      onSubmit({ ...formData, method: 'otp' });
    }
  };

  const handleResendOTP = () => {
    if (canResend) {
      setTimer(60);
      setCanResend(false);
      setFormData(prev => ({ ...prev, otp: '' }));
    }
  };

  if (step === 'phone') {
    return (
      <form onSubmit={handleSendOTP} className="space-y-6">
        <div>
          <Input
            label="Phone Number"
            type="tel"
            name="phone"
            placeholder="+1 (555) 123-4567"
            value={formData?.phone}
            onChange={handleChange}
            error={errors?.phone}
            required
            disabled={loading}
          />
        </div>
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={loading}
          iconName="Send"
          iconPosition="right"
        >
          Send OTP
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleVerifyOTP} className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Smartphone" size={24} className="text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Verify Your Phone</h3>
        <p className="text-sm text-muted-foreground">
          We've sent a 6-digit code to<br />
          <span className="font-medium text-foreground">{formData?.phone}</span>
        </p>
      </div>
      <div>
        <Input
          label="Verification Code"
          type="text"
          name="otp"
          placeholder="Enter 6-digit code"
          value={formData?.otp}
          onChange={handleChange}
          error={errors?.otp}
          required
          disabled={loading}
          maxLength={6}
        />
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {timer > 0 ? `Resend in ${timer}s` : 'Didn\'t receive code?'}
        </span>
        <button
          type="button"
          onClick={handleResendOTP}
          disabled={!canResend || loading}
          className="text-primary hover:text-primary/80 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
        >
          Resend OTP
        </button>
      </div>
      <div className="space-y-3">
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={loading}
          iconName="Check"
          iconPosition="right"
        >
          Verify & Sign In
        </Button>
        
        <Button
          type="button"
          variant="ghost"
          size="lg"
          fullWidth
          onClick={() => setStep('phone')}
          disabled={loading}
          iconName="ArrowLeft"
          iconPosition="left"
        >
          Change Phone Number
        </Button>
      </div>
    </form>
  );
};

export default OTPForm;